import { AttributeOffsets, type Offset } from "../engine/attributeOffsets";
import type { Bean, Grinder, Shot } from "../engine/model";
import {
  normalizeBean, normalizeGrinder, normalizeShot, type ConfigVersion, type LearningEntry, type Repositories, type StoredNudge,
} from "./repositories";

/**
 * JSON backup. This is the safety net against Safari clearing site data: the user exports a file to iCloud / Files
 * and imports it on a fresh install. Version 2 adds the learned offsets and the learning ledger; version 1 files
 * still import (offsets fall back to seeds, ledger empty, V3 record fields filled with defaults). Version 5 adds the
 * tuned config versions and the taste-nudge log; older files import with both empty.
 */
export const EXPORT_FORMAT = "pucktech-backup";
export const EXPORT_VERSION = 5;

export interface Backup {
  format: typeof EXPORT_FORMAT;
  version: number;
  exportedAt: string;
  grinders: Grinder[];
  beans: Bean[];
  shots: Shot[];
  settings: Record<string, string>;
  attributeOffsets: Offset[];
  beanLearning: LearningEntry[];
  configVersions: ConfigVersion[];
  tasteNudges: StoredNudge[];
}

export async function exportAll(repos: Repositories, exportedAt: string): Promise<Backup> {
  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    exportedAt,
    grinders: await repos.grinders.findAll(),
    beans: await repos.beans.findAll(),
    shots: await repos.shots.findAll(),
    settings: await repos.settings.entries(),
    attributeOffsets: (await repos.offsets.load()).entries(),
    beanLearning: await repos.learning.findAll(),
    configVersions: await repos.configVersions.findAll(),
    tasteNudges: await repos.nudges.findAll(),
  };
}

export type ImportMode = "replace" | "merge";

export interface ImportResult {
  grindersAdded: number;
  beansAdded: number;
  shotsAdded: number;
  shotsSkipped: number;
}

export function parseBackup(text: string): Backup {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error("Not a JSON file");
  }
  const b = raw as Partial<Backup>;
  if (b.format !== EXPORT_FORMAT) throw new Error("Not a Puck Tech backup");
  if (typeof b.version !== "number" || b.version > EXPORT_VERSION) throw new Error(`Unsupported backup version ${b.version}`);
  if (!Array.isArray(b.grinders) || !Array.isArray(b.beans) || !Array.isArray(b.shots)) throw new Error("Backup is incomplete");
  return {
    format: EXPORT_FORMAT,
    version: b.version,
    exportedAt: b.exportedAt ?? "",
    grinders: b.grinders.map(normalizeGrinder),
    beans: b.beans.map(normalizeBean),
    shots: b.shots.map(normalizeShot),
    settings: b.settings ?? {},
    attributeOffsets: Array.isArray(b.attributeOffsets) && b.attributeOffsets.length > 0 ? b.attributeOffsets : AttributeOffsets.seeds().entries(),
    beanLearning: Array.isArray(b.beanLearning) ? b.beanLearning : [],
    configVersions: Array.isArray(b.configVersions) ? b.configVersions : [],
    tasteNudges: Array.isArray(b.tasteNudges) ? b.tasteNudges : [],
  };
}

/**
 * replace: wipe everything and restore the backup verbatim, ids included (offsets and ledger too).
 * merge:   keep existing data; match grinders by name, beans by (name, roaster, roastDate), shots by
 *          (bean, grinder, timestamp); add what is missing, remapping ids. Settings, offsets, ledger and config
 *          versions are left alone; taste nudges come along only for beans the merge added.
 */
export async function importAll(repos: Repositories, backup: Backup, mode: ImportMode): Promise<ImportResult> {
  const result: ImportResult = { grindersAdded: 0, beansAdded: 0, shotsAdded: 0, shotsSkipped: 0 };

  if (mode === "replace") {
    await repos.clearAll();
    for (const g of backup.grinders) { await repos.grinders.save(g); result.grindersAdded++; }
    for (const b of backup.beans) { await repos.beans.save(b); result.beansAdded++; }
    for (const s of backup.shots) { await repos.shots.save(s); result.shotsAdded++; }
    for (const [k, v] of Object.entries(backup.settings)) await repos.settings.put(k, v);
    await repos.offsets.save(AttributeOffsets.of(backup.attributeOffsets));
    for (const e of backup.beanLearning) await repos.learning.markLearned(e.beanId, e.grinderId, e.learnedAt);
    for (const v of backup.configVersions) await repos.configVersions.save(v);
    for (const n of backup.tasteNudges) await repos.nudges.save(n);
    return result;
  }

  const grinderIdMap = new Map<number, number>();
  const existingGrinders = await repos.grinders.findAll();
  for (const g of backup.grinders) {
    const match = existingGrinders.find((e) => e.name === g.name);
    if (match) {
      grinderIdMap.set(g.id as number, match.id as number);
    } else {
      const saved = await repos.grinders.save({ ...g, id: null });
      existingGrinders.push(saved);
      grinderIdMap.set(g.id as number, saved.id as number);
      result.grindersAdded++;
    }
  }

  const beanIdMap = new Map<number, number>();
  const addedBeans = new Set<number>();
  const existingBeans = await repos.beans.findAll();
  const beanKey = (b: Bean) => `${b.name}\u0000${b.roaster ?? ""}\u0000${b.roastDate}`;
  for (const b of backup.beans) {
    const match = existingBeans.find((e) => beanKey(e) === beanKey(b));
    if (match) {
      beanIdMap.set(b.id as number, match.id as number);
    } else {
      const saved = await repos.beans.save({ ...b, id: null });
      existingBeans.push(saved);
      beanIdMap.set(b.id as number, saved.id as number);
      addedBeans.add(b.id as number);
      result.beansAdded++;
    }
  }

  const existingShots = await repos.shots.findAll();
  const shotKey = (s: Shot) => `${s.beanId}\u0000${s.grinderId}\u0000${s.timestamp}`;
  const seen = new Set(existingShots.map(shotKey));
  for (const s of backup.shots) {
    const beanId = beanIdMap.get(s.beanId);
    const grinderId = grinderIdMap.get(s.grinderId);
    if (beanId === undefined || grinderId === undefined) { result.shotsSkipped++; continue; }
    const remapped: Shot = { ...s, id: null, beanId, grinderId };
    const key = shotKey(remapped);
    if (seen.has(key)) { result.shotsSkipped++; continue; }
    await repos.shots.save(remapped);
    seen.add(key);
    result.shotsAdded++;
  }
  for (const n of backup.tasteNudges) {
    const beanId = beanIdMap.get(n.beanId);
    if (beanId !== undefined && addedBeans.has(n.beanId)) await repos.nudges.save({ ...n, id: null, beanId });
  }
  return result;
}
